import React, { Component, useContext, useEffect, useLayoutEffect, useState, useRef } from 'react';
import config from './../config';
import { fetchVoteEligibility, nextQueueIndex } from '../utils/voteEligibility';
import { auth_fetch } from './../utils/ajax';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardBody,
    CardFooter,
    CardHeader,
    FormCheckbox,
    FormRadio,
} from 'shards-react';
import PageTitle from './../components/common/PageTitle';
import SingleCellDisplay from './../components/wsi/SingleCellDisplay';
import WsiDisplay from './../components/wsi/WsiDisplay';
import DiagnosisContext from '../components/wsi/DiagnosisContext';
import useWsiMetadata from '../hooks/useWsiMetadata';
//import SingleCellAnnotation from "./../components/wsi/SingleCellAnnotation";
import { Cell, Dataset, LabelGroup, LabelSet, Label, Wsi, ExportGroup } from './../models/BackendModels';
import DatasetSelect from './../components/wsi/DatasetSelect';
import WsiSelect from './../components/wsi/WsiSelect';
import { UserContext } from './../context/UserContext';
import { checkSuccess } from './../utils/ajax';
import { type } from 'os';
import ClipLoader from 'react-spinners/ClipLoader';
import Input from 'react-select/src/components/Input';
import styled from 'styled-components';
import ExportGroupSelect, {
    retrieveExportGroup,
    retrieveExportGroups,
    retrieveExportGroupWsi,
} from '../components/wsi/ExportGroupSelect';
import GoogleDocEmbed from '../components/GoogleDocEmbed';

type CellId = number;

const SpacedCardBody = styled(CardBody)`
    display: flex;
    gap: 10px;
`;
const FilterButton = styled(Button)``;
// interface CellQueryResultItem {
//     id: number;
//     dataset: number;
//     wsi: number;
//     segmentationSet: number;
// }

interface FilterArgs {
    segmentationSetId?: number;
    segmentationSetIds?: number[];
    datasetId?: number;
    wsiId?: number;
    labeledBy?: Array<number | null>;
    labelSetId: number;
    labelGroupIds?: number[];
}

export function retrieveLabelSet(labelSetId: number): Promise<LabelSet> {
    return auth_fetch(`${config.backend}/api/labelset/${labelSetId}/`).then(checkSuccess);
}

export const getPaginatedCellFromCellFilterSet = (
    cellFilterSetId: number,
    annotatorPriority: (number | null)[],
    userId: number | null = null,
    cellIdx: number,
): Promise<CellId[]> =>
    auth_fetch(`${config.backend}/api/cell_filterset/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            cellFilterSetId: cellFilterSetId,
            annotatorPriority: annotatorPriority,
            tailoredForAnnotator: userId,
            cellIdx: cellIdx,
        }),
    }).then(checkSuccess);


export const retrieveCellFilterSet = (
    cellFilterSetId: number,
    annotatorPriority: (number | null)[],
    userId: number | null = null,
    targetQueue: string = "full_queue",
): Promise<CellId[]> =>
    auth_fetch(`${config.backend}/api/cell_filterset/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            cellFilterSetId: cellFilterSetId,
            annotatorPriority: annotatorPriority,
            tailoredForAnnotator: userId,
            targetQueue: targetQueue,
        }),
    }).then(checkSuccess);

export const retrieveWsi = (wsiId: number): Promise<Wsi> =>
    auth_fetch(`${config.backend}/api/wsi/${wsiId}`)
        .then(checkSuccess)
        .then((data) => data[0]);

const fetchCellQueryResult = (filterArgs: FilterArgs): Promise<CellId[]> => {
    return auth_fetch(`${config.backend}/api/query_cells/`, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(filterArgs),
    }).then(checkSuccess);
};

const fetchCell = (cellId: number): Promise<Cell> => {
    return auth_fetch(`${config.backend}/api/cell/${cellId}/`).then(checkSuccess);
};

const fetchAnnotationCount = (
    userId: number,
    segmentationSetId: number,
    labelGroupId: number | undefined = undefined,
): Promise<number> => {
    return auth_fetch(
        `${config.backend}/api/cell_label_selections_count/${userId}/${segmentationSetId}/${
            typeof labelGroupId !== 'undefined' ? labelGroupId + '/' : ''
        }`,
    ).then(checkSuccess);
};

interface Option {
    id: number | null;
    name: string;
}
// const VALID_ANNOTATOR_OPTIONS = [
//     {
//         id: null,
//         name: 'Nobody',
//     },
//     {
//         id: 12,
//         name: 'Junker Jack',
//     },
//     {
//         id: 26,
//         name: 'Blair Brook',
//     },
//     {
//         id: 29,
//         name: 'Casey Carr',
//     },
//     {
//         id: 30,
//         name: 'Riley Ray',
//     },
// ];

const FIXED_SUGGESTIONS = [
    {
        id: 13,
        name: 'Cell',
        labelGroup: 8,
    },
    {
        id: 6,
        name: 'Erythrocyte',
        labelGroup: 5,
    },
];

// const FIXED_EXPORT_GROUPS = [44, 45];

const toggleSidebar = () => {
    const sidebar = document.querySelectorAll<HTMLElement>('.main-sidebar')[0];
    if (sidebar.style.transform === `translateX(-100%)`) {
        sidebar.style.transform = 'translateX(0)';
    } else {
        sidebar.style.transform = 'translateX(-100%)';
    }
};


export function fetchLabelGroups(labelSetId: any) {
    return auth_fetch(`${config.backend}/api/label_groups/${labelSetId}/`)
        .then(checkSuccess)
        .then((data) => data.sort((a: any, b: any) => a.sortOrder - b.sortOrder));
}

export function fetchLabels(labelSetId: number) {
    return auth_fetch(`${config.backend}/api/labels/${labelSetId}/`).then(checkSuccess);
}

const CELL_TYPE_LABEL_GROUP = 5;
const LINEAGE_LABEL_GROUP = 9;
// Add this styled overlay component
const LoadingOverlay = styled.div`
    position: fixed; /* or absolute if you want to limit to a container */
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(255,255,255,0.7);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
`;

const PreselectedCellAnnotation: React.FC = () => {
    const { user } = useContext(UserContext);
    // const [availableExportGroups, setAvailableExportGroups] = useState<ExportGroup[]>([]);
    // const [selectedExportGroup, setSelectedExportGroup] = useState<ExportGroup>();
    // const [availableWsi, setAvailableWsi] = useState<Wsi[]>([]);
    // const [labelSet, setLabelSet] = useState<LabelSet>();
    // const [labeledBy, setLabeledBy] = useState([]);
    const [suggestions, setSuggestions] = useState<Label[]>(FIXED_SUGGESTIONS);
    // const [validFilterOptions, setValidFilterOptions] = useState<Set<Option>>(new Set(VALID_ANNOTATOR_OPTIONS));
    // const [priorityOption, setPriorityOption] = useState<Option>(); // VALID_ANNOTATOR_OPTIONS[0]
    const [myLabeledCells, setMyLabeledCells] = useState<CellId[]>([]);
    const [cellsFilter, setCellsFilter] = useState<CellId[]>([]);
    const [loaded, setLoaded] = useState(false);
    const [autoSkipLabeledByMe, setAutoSkipLabeledByMe] = useState(true);
    const [skipLabeledByMe, setSkipLabeledByMe] = useState(false);
    const [selectedCellIndex, setSelectedCellIndex] = useState(0);
    const [selectedCell, setSelectedCell] = useState<Cell>();
    const { wsi: selectedWsi, loading: metadataLoading } = useWsiMetadata(selectedCell?.wsi, selectedCell?.id);
    const pendingNavigationScroll = useRef<number>();
    useLayoutEffect(() => {
        if (!loaded || metadataLoading || pendingNavigationScroll.current === undefined ||
            selectedCell?.id !== pendingNavigationScroll.current) return;
        pendingNavigationScroll.current = undefined;
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, [loaded, metadataLoading, selectedCell?.id]);
    const [navigationError, setNavigationError] = useState('');
    const [taskNotice, setTaskNotice] = useState('');
    const navigationVersion = useRef(0);
    const retryNavigation = useRef<(() => Promise<void>) | null>(null);
    // const [cells, setCells] = useState<Cell[]>([]);

    const [labels, setLabels] = useState<Label[]>([]);
    const [labelGroups, setLabelGroups] = useState<LabelGroup[]>([]);

    const retrieveCellsFilter = async () => {
        return retrieveCellFilterSet(4, [], user.id, "full_queue");
        // fetchAnnotationCount(user.id, config.latestSegmentationSetId).then(setAnnotationCount);
        // fetchAnnotationCount(user.id, config.latestSegmentationSetId, CELL_TYPE_LABEL_GROUP).then(
        //     setCellTypeAnnotationCount,
        // );
    };

    const retrieveMyLabeledCells = async () => {
        return retrieveCellFilterSet(4, [], user.id, "already_labeled_cells");
    }


    const updateSelectedCell = async (cellId: number) => {
        const cell = await fetchCell(cellId);
        setSelectedCell(cell);
        return cell;
    }

    const navigateToCell = async (newIndex: number, direction = 0, scrollToTop = false) => {
        const version = ++navigationVersion.current;
        retryNavigation.current = () => navigateToCell(newIndex, direction, scrollToTop);
        setLoaded(false);
        setNavigationError('');
        try {
            const freshQueue = await retrieveCellsFilter();
            if (!Array.isArray(freshQueue)) throw new Error('Queue is still being generated');
            let index = direction && selectedCell
                ? nextQueueIndex(cellsFilter, freshQueue, selectedCell.id, direction)
                : Math.max(0, newIndex);
            while (index >= 0 && index < freshQueue.length) {
                const id = freshQueue[index];
                const status = await fetchVoteEligibility(id, user.id);
                if (version !== navigationVersion.current) return;
                if (!status.eligible) {
                    freshQueue.splice(index, 1);
                    if (direction < 0) index--;
                    continue;
                }
                const next = await fetchCell(id);
                if (version !== navigationVersion.current) return;
                setCellsFilter(freshQueue);
                setSelectedCellIndex(index);
                pendingNavigationScroll.current = scrollToTop ? next.id : undefined;
                setSelectedCell(next);
                setLoaded(true);
                return;
            }
            if (version !== navigationVersion.current) return;
            setCellsFilter(freshQueue);
            setSelectedCell(undefined);
            setTaskNotice('No available tasks remain in this direction.');
            setLoaded(true);
        } catch (error) {
            if (version !== navigationVersion.current) return;
            setNavigationError('Could not load the next task. Retry to continue.');
            setLoaded(true);
        }
    };

    useEffect(() => {
        let active = true;
        setLoaded(false);
        const init = async () => {
            try {
                const [groups, allLabels, queue, history] = await Promise.all([
                    fetchLabelGroups(config.labelSet), fetchLabels(config.labelSet), retrieveCellsFilter(), retrieveMyLabeledCells(),
                ]);
                if (!active) return;
                if (!Array.isArray(queue) || !Array.isArray(history)) throw new Error('Queue is still being generated');
                setLabelGroups(groups);
                setLabels(allLabels);
                setMyLabeledCells(history);
                setAutoSkipLabeledByMe(false);
                const firstPending = queue.findIndex(id => !history.includes(id));
                await navigateToCell(firstPending < 0 ? queue.length : firstPending);
            } catch (error) {
                if (!active) return;
                setNavigationError('Could not load tasks. Retry to continue.');
                retryNavigation.current = init;
                setLoaded(true);
            }
        };
        init();
        return () => { active = false; navigationVersion.current++; };
    }, []);

    // const [cellTypeAnnotationCount, setCellTypeAnnotationCount] = useState<number>(0);
    // const [annotationCount, setAnnotationCount] = useState<number>(0);
    // const [me, setMe] = useState<Option>();

    // useEffect(() => {
    //     if (typeof user !== 'undefined') {
    //         let my = VALID_ANNOTATOR_OPTIONS.find(u => u.id === user.id);
    //         // setMe({...user, name: "Me"});
    //         const me = {...user, name: `Me ${typeof my !== 'undefined' ? '('+my.name+')' : ''}`};
    //         setValidFilterOptions(new Set(VALID_ANNOTATOR_OPTIONS.filter(u => u.id !== user.id).concat(me)));
    //     }
    // }, [user]);

    // useEffect(() => {
    //     retrieveLabelSet(config.labelSet).then((labelSet: LabelSet) => {
    //         setLabelSet(labelSet);
    //         // debugger;
    //     });
    // }, []);

    // useEffect(() => {
    // }, []);

    // useEffect(() => {
    //     if (typeof priorityOption !== 'undefined') {
    //         setLoaded(false);
    //         retrieveCellFilterSet(1, [priorityOption.id]).then(setCellsFilter);
    //     }
    // }, [priorityOption]);

    // useEffect(() => {
    //     if (typeof user !== 'undefined') {
    //         setPriorityOption(
    //             user.id === 29
    //                 ? VALID_ANNOTATOR_OPTIONS.find((o) => o.id === 30)
    //                 : VALID_ANNOTATOR_OPTIONS.find((o) => o.id === 29),
    //         );
    //     }
    // }, [user]);

    // useEffect(() => {

    // }, [cellsFilter, myLabeledCells]);

    // useEffect(() => {
    //     if (typeof cellsFilter !== 'undefined' && cellsFilter.length > 0) {
    //         setLoaded(false);

    //     }
    // }, [cellsFilter]);

    // useEffect(() => {
    //     if (typeof myLabeledCells !== 'undefined' && myLabeledCells.length > 0) {
    //         setSelectedCellIndex(cellsFilter.findIndex((id) => myLabeledCells.indexOf(id) === -1));
    //     }
    // }, [cellsFilter, myLabeledCells]);

    // useEffect(() => {
    //     if (typeof cellsFilter !== 'undefined') {
    //         if (cellsFilter.length > 0 && typeof cellsFilter[selectedCellIndex] !== 'undefined') {
    //             if (skipLabeledByMe && myLabeledCells.indexOf(cellsFilter[selectedCellIndex]) !== -1) {
    //                 nextCell();
    //             } else {
    //                 setLoaded(false);
    //                 fetchCell(cellsFilter[selectedCellIndex]).then((cell) => {
    //                     setCells([cell]);
    //                     setSelectedCell(cell);
    //                 });
    //             }
    //             // .then(setSelectedCell)
    //             // .then(() => setFinalLoaded(true))
    //             // .then(() => setLoaded(true));
    //         } else {
    //             // setCells([]);
    //             // setSelectedCellIndex(0);
    //             // setSelectedCell(undefined);
    //             // setFinalLoaded(true);
    //             // setLoaded(true);
    //         }
    //     }
    // }, [cellsFilter, selectedCellIndex]);



    // useEffect(() => {
    // }, [user]);

    const previousCell = () => {
        let newIndex = selectedCellIndex - 1;
        while (skipLabeledByMe && myLabeledCells.indexOf(cellsFilter[newIndex]) !== -1) {
            newIndex--;
        }
        navigateToCell(newIndex, -1, true);
    };

    const nextCell = () => {
        let newIndex = selectedCellIndex + 1;
        while (skipLabeledByMe && myLabeledCells.indexOf(cellsFilter[newIndex]) !== -1) {
            newIndex++;
        }
        navigateToCell(newIndex, 1, true);
    };

    const jumpToCell = (idx: number) => {
        navigateToCell(idx - 1, 0, true);
    };

    // const annotatedAsCellWithoutTypeCount = annotationCount - cellTypeAnnotationCount;

    return (
        <Container fluid className="main-content-container px-4" style={{ marginTop: '12vh', position: 'relative' }}>
            {navigationError && <div role="alert">{navigationError} <Button onClick={() => retryNavigation.current?.()}>Retry task</Button></div>}
            {taskNotice && <div role="status">{taskNotice}</div>}
            {/* Overlay spinner */}
            { (!loaded || metadataLoading) && (
                <LoadingOverlay>
                    <ClipLoader loading={true} size={150} />
                </LoadingOverlay>
            )}
            {/* Remove the old spinner div */}
            {/* <div style={{ textAlign: 'center' }}>
                <ClipLoader loading={!loaded} size={150} />
            </div> */}
            <DiagnosisContext context={selectedWsi} />
            <Row>
                <Col className="d-block mb-4">
                    {/* d-none d-lg-block */}
                    {typeof selectedWsi !== 'undefined' && (
                        <WsiDisplay
                            title="Image overlay"
                            entityLabel="image"
                            wsi={selectedWsi}
                            loaded={true}
                            downsize={true}
                            cells={[selectedCell]}
                            pixelDiameterInMicrometer={Number(selectedWsi.pixelDiameterInMicrometer)}
                            selectedCell={selectedCell}
                            onClick={(cell: Cell) => setSelectedCellIndex(cellsFilter.indexOf(cell.id))}
                        />
                    )}
                </Col>
            </Row>
            <Row>
                <Col lg="12" md="12" sm="12" className="mb-4">
                    {typeof selectedCell !== 'undefined' &&
                        labels.length > 0 && (
                            <SingleCellDisplay
                                pixelDiameterInMicrometer={Number(selectedWsi?.pixelDiameterInMicrometer ?? NaN)}
                                title={`Cell ${selectedCellIndex + 1} of ${cellsFilter.length} `}
                                user={user}
                                cell={selectedCell}
                                disabled={!loaded || metadataLoading}
                                showPrevious={selectedCellIndex > 0}
                                showNext={selectedCellIndex < cellsFilter.length - 1}
                                onPrevious={previousCell}
                                onNext={nextCell}
                                onCellComplete={(id: number) => {
                                    if (selectedCell?.id !== id) return;
                                    setTaskNotice('A completed task was skipped. A new vote was not saved.');
                                    navigateToCell(selectedCellIndex + 1, 1);
                                }}
                                onJumpTo={jumpToCell}
                                suggestions={suggestions}
                                // labelSetId={labelSet.id}
                                annotationCountText={`UNUSED`}
                                allowFreetext={true}
                                loaded={false}
                                hiddenLabelGroups={[9,11, 12]}
                                labels={labels}
                                labelGroups={labelGroups}
                            />
                        )}
                </Col>
            </Row>
            <GoogleDocEmbed 
                title="Annotation Guide"
                docUrl="https://docs.google.com/document/d/e/2PACX-1vSH-5xxmEc283KtDC5z_v2Ydczu6LUz6ok_0W28A462tofdmhSi4AiPxNhcAwBsNI_XdHAPxY4yLAIt/pub?embedded=true"
            />
        </Container>
    );
};

export default PreselectedCellAnnotation;
